import { ReactNode } from 'react';

export type TSort = 'ascending' | 'descending' | 'none';

export type TListStatus = 'loading' | 'error' | 'loaded' | null;

export interface INewTableColumn<T> {
    /** Distinct id for columm. */
    id: string;
    /** Column title. will be shown as default if renderHeader not given. */
    title?: string | ReactNode;
    /** Indicates whether the column is sortable. */
    isSortable?: boolean;
    /**
     * Render function of a cell in the table.
     * @param item - The row item.
     * @param isRowHovered - The whole row is hovered.
     * @param isRowExpanded - The row is expanded.
     * @param setIsRowExpanded - Function to expand / collapse the row.
     */
    renderCell: (
        item: T,
        { isRowHovered, isRowExpanded, setIsRowExpanded }
    ) => ReactNode;
    /**
     * Render function of a header in the table. If won't given, then will render nothing.
     * @param column - The column. This is relevant for an outer component that doesn't know which column is it.
     */
    renderHeader?: ({ column }: { column: INewTableColumn<T> }) => ReactNode;
    /** Content of column header tooltip. */
    headerTooltip?: ReactNode;
    /** Content of sort icon tooltip. */
    sortTooltip?: ReactNode;
    /** Width of column. can be fixed in px. % is relative to whole table. */
    width?: string;
}

export interface INewTableSelectableProps<T> {
    /** A list of selected items ids. This data is handled outside the component. */
    selectedItemIds: string[];
    /** Will happen when toggling the item row checkbox
     * @param item - the current row item.
     * @param isSelected - the value of checked / unchecked.
     */
    onSelectItem: (item: T, isSelected: boolean) => void;
    /** An action to select all items. */
    selectAll: () => void;
    /** An action to clear all selections. */
    clearSelections: () => void;
    /** A custom render component function to show in the selectable header row. */
    renderBulkActionsComponent?: () => ReactNode;
    /** Indicates whether all items are selected. This data is handled outside the component. */
    allItemsSelected?: boolean;
    /** Temp solution for edgecase until new table component is out.
     *  Indicates if the header row will be kept shown when there's a selection.
     *  should not be used with sort or bulk actions */
    showTableHeaderOnSelect?: boolean;
}

export interface IInfiniteScrollProps {
    /** Indicates if there's more data to show that is not shown yet. Should be provided from the server */
    hasMore: boolean;
    /** The load more function. Here is the place to change the call as you need - sort, search and more.
     * @param page - the next page to fetch. Provided from the component itself.
     */
    loadMore: (page: number) => void;
    /** Indicates the scroll threshold - from how many pixels from bottom, the next page will be fetched. */
    threshold?: number;
    /** A custom loader component. If not given, will use a design-system loader. */
    loader?: ReactNode;
    /** The page to start the fetch from. Usually, we will fetch the first page from outside the infiscroll and from the next page it will load more by itself. */
    pageStart?: number;
}

export interface INewTableExpandableProps<T> {
    /** Expanded row render component function. */
    renderComponent: (item: T) => ReactNode;
    /** When true, clicking anywhere on the row toggles expansion; disabled automatically in selection mode. */
    expandOnRowClick?: boolean;
    /** Optional callback function to run on expand  */
    onExpand?: (nextState: boolean, item: T) => void;
    /** Optional  Indicates if every row should show the "up" and "down" expand icon  */
    shouldShowExpandColumn?: boolean;
}

export interface INoResultsProps {
    title: string;
    subtitle: string;
}

export interface IPinProps {
    /** Pin first column. */
    isFirstColumnPinned?: boolean;
    /** Pin last column. */
    isLastColumnPinned?: boolean;
    /** Pinned column header background color */
    pinnedColumnHeaderBgColor?: string;
}

export interface INewTableProps<T> {
    /** Items that will be tranlated to table rows. */
    items: T[];
    /** Table column definitions. */
    columnDefinitions: INewTableColumn<T>[];
    /** Get item id from an item. This is used mostly for creating component keys. */
    getItemId: (item: T) => string;
    /** Selectable props. If not given, then multi-select will be disabled. */
    selectableProps?: INewTableSelectableProps<T>;
    /** Infinite scroll props. If not given, then will load all data at one call. */
    infiniteScrollProps?: IInfiniteScrollProps;
    /** Column pinning props. */
    pinProps?: IPinProps;
    /** Expanded row props. If not given, so rows won't be expandable. */
    expandableProps?: INewTableExpandableProps<T>;
    /** Optional space between rows. */
    hasSpacer?: boolean;
    /** Optional aria label. */
    ariaLabel?: string;
    /** Optional data-tour-id */
    tourId?: string;
    /** Props for the No Results state */
    noResultsProps?: INoResultsProps;
    /** CSS max-height for the scroll container (e.g. '400px'); content scrolls vertically when exceeded. */
    maxHeight?: string;
    /** When true, renders a native `<table>` element instead of the default div-based `role="table"` layout. */
    useNativeTable?: boolean;
}

export const INFINITE_SCROLL_THRESHOLD = 250;
